import { Component, inject, Input, EventEmitter, Output, Inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, AppConfig } from '../../app.config';
import { globalMessages } from '../shared/global-messages';
import { CardLayoutComponent, CardSection, CardItem } from '../shared/card-layout/card-layout.component';
import { ModalService } from '../shared/modal.service';
import { CardField } from '../shared/card-layout/card-layout.component';
import { EditContactService } from '../services/edit-contact.service';
import { ToastService } from '../shared/toast/toast.service';
import { TrackingService } from '../tracking/tracking.service';
import * as PolicyActions from '../store/policy/policy.actions';
import moment from 'moment';

const LOB_CONFIG: Record<string, { icon: string; title: string }> = {
    HO3: { icon: 'home-icon', title: 'Homeowners' },
    HO4: { icon: 'renters-icon', title: 'Renters' },
    DP1: { icon: 'basic-dwelling', title: 'Basic Dwelling' },
    DP3: { icon: 'special-dwelling', title: 'Special Dwelling' },
    HO6: { icon: 'condo-icon', title: 'Condo' },
    MCST: { icon: 'Motorcycle', title: 'Motorcycle' },
    MCOR: { icon: 'off-road', title: 'ATV / Dirt Bike' },
};

@Component({
    selector: 'app-insured-profile',
    imports: [CommonModule, CardLayoutComponent],
    template: `
        <app-card-layout
            [isActive]="isActive"
            title="Insured profile"
            [isLoading]="isDCPolicyLoaded"
            [failed]="dcPolicyFailed"
            [sections]="sections"
            [items]="[...additionalInsuredItems, ...thirdPartyGroupItems]"
            [noDataMessage]="globalMessages.noDataAvailabelMsg"
            (retry)="retryDCPolicy()"
            (actionIconClick)="openEditPopup($any($event))"
        ></app-card-layout>
    `,
})
export class InsuredProfileComponent {
    isActive = false;
    @Input() isDCPolicyLoaded = false;
    @Input() dcPolicyFailed = false;
    @Input() isThirdParty = false;
    editLoading = false;
    updatingField = signal<Set<'phone' | 'email'>>(new Set());
    @Output() retry = new EventEmitter<void>();
    globalMessages = globalMessages;
    private store = inject(Store);
    policySignal = this.store.selectSignal((state: any) => state.policy);

    constructor(
        @Inject(APP_CONFIG) private config: AppConfig,
        private modalService: ModalService,
        private editContactService: EditContactService,
        private toastService: ToastService,
        private http: HttpClient,
        private trackingService: TrackingService
    ) {}

    @Input() set policyData(_: any) {}

    /** Returns true for ownership types that share LLC-style edit restrictions (LLC and Trust). */
    private isRestrictedOwnership(ownership: string | undefined): boolean {
        return ownership === 'LLC' || ownership === 'Trust';
    }

    private formatDate(value: string): string {
        if (!value) return '';
        const [year, month, day] = value.split('-');
        return `${month}/${day}/${year}`;
    }

    private phoneTag(
        valid: boolean | null,
        cellular: boolean | null
    ): { successTag?: string; warningTag?: string; errorTag?: string } {
        if (valid == null) return {};
        if (!valid) return { errorTag: 'Invalid' };
        return cellular ? { successTag: 'Cellular' } : { warningTag: 'Non-Cellular' };
    }

    private getCoOwnerFields(insuredInfo: any, coOwnerValidation: any[], isThirdParty = false): any[] {
        const fields: any[] = [];
        let validationIndex = 0;
        for (let i = 1; i <= 3; i++) {
            const firstName = insuredInfo?.[`coOwner${i}Firstname`]?.value;
            if (!firstName) continue;
            const v = coOwnerValidation?.[validationIndex++];
            fields.push(
                { caption: `CO-OWNER (${i})`, value: '', captionHtml: `<h6>CO-OWNER (${i})</h6>`, hasSeparator: true },
                {
                    caption: 'Full name',
                    value: [
                        firstName,
                        insuredInfo?.[`coOwner${i}MiddleInitial`]?.value,
                        insuredInfo?.[`coOwner${i}Lastname`]?.value,
                    ]
                        .filter(Boolean)
                        .join(' '),
                },
                ...(!isThirdParty
                    ? [
                          {
                              caption: 'Date of birth',
                              value: this.formatDate(insuredInfo?.[`coOwner${i}BirthDate`]?.value),
                          },
                      ]
                    : []),
                {
                    caption: 'Phone',
                    value: insuredInfo?.[`coOwner${i}Phone`]?.value || '-',
                    ...this.phoneTag(v?.phoneValid, v?.phoneCellular),
                },
                {
                    caption: 'Email',
                    value: insuredInfo?.[`coOwner${i}Email`]?.value || '-',
                    successTag: v?.emailValid === true ? 'Valid' : undefined,
                    errorTag: v?.emailValid === false ? 'Invalid' : undefined,
                }
            );
        }
        return fields;
    }

    get sections(): CardSection[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const data = policyState.data?.[policyNum]?.policySnapshot;
        if (!data) return [];

        const lob = data?.policyGeneralInformation?.lineOfBusiness?.value ?? '';
        const lobConfig = LOB_CONFIG[lob];
        const insuredFullName = data?.policyGeneralInformation?.name?.value || '';

        const policyStatusValue = data?.policyGeneralInformation?.policyStatus?.value?.toLowerCase();
        const isCancelled = policyStatusValue === 'cancelled' || policyStatusValue === 'expired';
        const isLLC = this.isRestrictedOwnership(data?.policyGeneralInformation?.ownership?.value);

        const allSections: CardSection[] = [
            {
                title: 'Named insured',
                icon: 'named-insured',
                fields: [
                    { caption: 'Full name', value: insuredFullName },
                    ...(!this.isThirdParty
                        ? [
                              {
                                  caption: 'Date of birth',
                                  value: this.formatDate(
                                      data?.contactPreferences?.insuredInformation?.BirthDate?.value
                                  ),
                              },
                          ]
                        : []),
                    {
                        caption: 'Mailing address',
                        value:
                            data?.contactPreferences?.insuredInformation?.mailingAddressDifferent?.value?.toLowerCase() ===
                            'yes'
                                ? data?.contactPreferences?.insuredInformation?.mailingFullAddress?.value
                                : data?.policyGeneralInformation?.propertyFullAddress?.value,
                    },
                    {
                        caption: 'Phone number',
                        value: data?.contactPreferences?.insuredInformation?.phone?.value,
                        isUpdating: this.updatingField().has('phone'),
                        // LLC ownership: hide edit icon for phone — user cannot update phone for LLC
                        actionIcon: this.isThirdParty || isLLC ? undefined : 'edit',
                        actionIconDisabled: isCancelled,
                        ...this.phoneTag(
                            policyState.data?.[policyNum]?.contactValidation?.insuredPhoneValid,
                            policyState.data?.[policyNum]?.contactValidation?.insuredPhoneCellular
                        ),
                    },

                    {
                        caption: 'Email',
                        value: data?.contactPreferences?.insuredInformation?.email?.value,
                        isUpdating: this.updatingField().has('email'),
                        // LLC ownership: email edit icon is always visible — user can still update email
                        actionIcon: this.isThirdParty ? undefined : 'edit',
                        actionIconDisabled: isCancelled,
                        successTag:
                            policyState.data?.[policyNum]?.contactValidation?.insuredEmailValid === true
                                ? 'Valid'
                                : undefined,
                        errorTag:
                            policyState.data?.[policyNum]?.contactValidation?.insuredEmailValid === false
                                ? 'Invalid'
                                : undefined,
                    },
                    ...this.getCoOwnerFields(
                        data?.contactPreferences?.insuredInformation,
                        policyState.data?.[policyNum]?.contactValidation?.coOwnerValidation ?? [],
                        this.isThirdParty
                    ),
                ],
            },
            {
                title: lobConfig?.title ?? 'Homeowners',
                icon: lobConfig?.icon ?? 'home-icon',
                fields: [
                    {
                        caption: 'Status',
                        value: data?.policyGeneralInformation?.policyStatus?.value,
                        isTag: true,
                        tagTheme: this.getStatusTheme(data?.policyGeneralInformation?.policyStatus?.value),
                    },
                    { caption: 'Policy number', value: data?.policyGeneralInformation?.policyNumber?.value },
                    { caption: 'Property address', value: data?.policyGeneralInformation?.propertyFullAddress?.value },
                    { caption: 'Vesting Title', value: data?.policyGeneralInformation?.vestingTitle?.value },
                ],
            },
            {
                title: 'Producer info',
                icon: 'customer-service',
                fields: [
                    {
                        caption: 'Producer code',
                        value: data?.agencyInformation?.agencyCode,
                        link: this.isThirdParty
                            ? undefined
                            : this.config.amsPortalUrl.replace(
                                  '<producerCode>',
                                  data?.agencyInformation?.agencyCode ?? ''
                              ),
                    },
                    { caption: 'Agency name', value: data?.agencyInformation?.agencyName?.value },
                    {
                        caption: 'Phone number',
                        value: data?.agencyInformation?.phoneNumber?.value,
                        ...this.phoneTag(
                            policyState.data?.[policyNum]?.contactValidation?.producerPhoneValid,
                            policyState.data?.[policyNum]?.contactValidation?.producerPhoneCellular
                        ),
                    },
                    {
                        caption: 'Email',
                        value: data?.agencyInformation?.contactEmail?.value,
                        successTag:
                            policyState.data?.[policyNum]?.contactValidation?.producerEmailValid === true
                                ? 'Valid'
                                : undefined,
                        errorTag:
                            policyState.data?.[policyNum]?.contactValidation?.producerEmailValid === false
                                ? 'Invalid'
                                : undefined,
                    },
                ],
            },
        ];

        return allSections.filter((section) => section.fields.some((f) => f.value));
    }

    get additionalInsuredItems(): CardItem[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const data = policyState.data?.[policyNum]?.policySnapshot;
        if (!data) return [];

        const isLLC = this.isRestrictedOwnership(data?.policyGeneralInformation?.ownership?.value);
        const policyStatusValue = data?.policyGeneralInformation?.policyStatus?.value?.toLowerCase();
        const isCancelled = policyStatusValue === 'cancelled' || policyStatusValue === 'expired';
        const additionalInsured = data?.contactPreferences?.additionalNamedInsured ?? [];

        return additionalInsured.map((ai: any, index: number) => {
            const addressParts = [
                [ai?.address1?.value, ai?.address2?.value].filter(Boolean).join(' '),
                ai?.city?.value,
                [ai?.state?.value, ai?.zipCode?.value].filter(Boolean).join(' '),
            ]
                .filter(Boolean)
                .join(', ');
            const aiValidation = policyState.data?.[policyNum]?.contactValidation?.additionalInsuredValidation?.[index];

            // Edit icon on phone + email only for the first Additional Insured when ownership is LLC
            // Hidden for third-party login users
            const isFirstLLC = isLLC && index === 0 && !this.isThirdParty;

            return {
                label: `Additional insured `,
                title: `ADDITIONAL INSURED (${index + 1})`,
                icon: 'additional-insured',
                fields: [
                    { caption: 'Name', value: `${ai?.firstName?.value} ${ai?.lastName?.value}` || '-' },
                    { caption: 'Address', value: addressParts || '-' },
                    { caption: 'Date of birth', value: this.formatDate(ai?.birthDate?.value) || '-' },
                    {
                        caption: 'Phone',
                        value: ai?.phoneNumber?.value || '-',
                        // LLC first entry: edit icon on phone only — user can update phone number
                        actionIcon: isFirstLLC ? 'edit' : undefined,
                        actionIconDisabled: isCancelled,
                        isUpdating: isFirstLLC ? this.updatingField().has('phone') : false,
                        // actionContext identifies this click as LLC additional insured phone edit
                        actionContext: isFirstLLC ? 'llcAdditionalInsured' : undefined,
                        ...this.phoneTag(aiValidation?.phoneValid, aiValidation?.phoneCellular),
                    },
                    {
                        caption: 'Email',
                        value: ai?.email?.value || '-',
                        // No edit icon on email for additional insured — email is not editable here
                        successTag: aiValidation?.emailValid === true ? 'Valid' : undefined,
                        errorTag: aiValidation?.emailValid === false ? 'Invalid' : undefined,
                    },
                ],
            };
        });
    }

    get thirdPartyItems(): CardItem[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const data = policyState.data?.[policyNum]?.policySnapshot;
        if (!data) return [];

        const thirdParties = data?.contactPreferences?.additionalInterest ?? [];
        return thirdParties.map((tp: any, index: number) => {
            const addressParts = [
                [tp?.address1?.value, tp?.address2?.value].filter(Boolean).join(' '),
                tp?.city?.value,
                [tp?.state?.value, tp?.zipCode?.value].filter(Boolean).join(' '),
            ]
                .filter(Boolean)
                .join(', ');
            const atValidation =
                policyState.data?.[policyNum]?.contactValidation?.additionalInterestValidation?.[index];

            return {
                title: `ADDITIONAL INTEREST (${index + 1})`,
                fields: [
                    { caption: 'Contact name', value: tp?.name?.value || '-' },
                    {
                        caption: 'Email',
                        value: tp?.email?.value || '-',
                        successTag: atValidation?.emailValid === true ? 'Valid' : undefined,
                        errorTag: atValidation?.emailValid === false ? 'Invalid' : undefined,
                    },
                    { caption: 'Address', value: addressParts || '-' },
                ],
            };
        });
    }

    get designatedThirdPartyItems(): CardItem[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const data = policyState.data?.[policyNum]?.policySnapshot;
        if (!data) return [];

        const designatedThirdParties = data?.contactPreferences?.designatedThirdParty ?? [];
        return designatedThirdParties.map((dtp: any, index: number) => {
            const dtpValidation =
                policyState.data?.[policyNum]?.contactValidation?.designatedThirdPartyValidation?.[index];
            return {
                title: `DESIGNATED THIRD PARTY (${index + 1})`,
                fields: [
                    { caption: 'Contact name', value: dtp?.name?.value || '-' },
                    {
                        caption: 'Email',
                        value: dtp?.email?.value || '-',
                        successTag: dtpValidation?.emailValid === true ? 'Valid' : undefined,
                        errorTag: dtpValidation?.emailValid === false ? 'Invalid' : undefined,
                    },
                    { caption: 'Address', value: dtp?.address?.value || '-' },
                ],
            };
        });
    }

    get mortgageItems(): CardItem[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const data = policyState.data?.[policyNum]?.policySnapshot;
        if (!data) return [];

        const mortgages = data?.mortgageInformation ?? [];
        return mortgages.map((mc: any, index: number) => {
            const addressParts = [
                [mc?.address1?.value, mc?.address2?.value].filter(Boolean).join(' '),
                mc?.city?.value,
                [mc?.state?.value, mc?.zipCode?.value].filter(Boolean).join(' '),
            ]
                .filter(Boolean)
                .join(', ');

            return {
                title: `MORTGAGE COMPANY (${index + 1})`,
                fields: [
                    { caption: 'Company name', value: mc?.name?.value || '-' },
                    { caption: 'Loan number', value: mc?.loanNumber?.value || '-' },
                    { caption: 'Address', value: addressParts || '-' },
                ],
            };
        });
    }

    get escrowItems(): CardItem[] {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const data = policyState.data?.[policyNum]?.policySnapshot;
        if (!data) return [];

        const escrows = data?.contactPreferences?.escrow ?? [];
        return escrows.map((esc: any, index: number) => {
            const escValidation =
                policyState.data?.[policyNum]?.contactValidation?.escrowValidation?.[index];
            return {
                title: `ESCROW (${index + 1})`,
                fields: [
                    { caption: 'Company name', value: esc?.companyName?.value || '-' },
                    {
                        caption: 'Email',
                        value: esc?.email?.value || '-',
                        successTag: escValidation?.emailValid === true ? 'Valid' : undefined,
                        errorTag: escValidation?.emailValid === false ? 'Invalid' : undefined,
                    },
                    { caption: 'Address', value: esc?.address?.value || '-' },
                ],
            };
        });
    }

    // Combines additional interest, designated third party, mortgage company, and escrow into one
    // visual group that shares a single "Third party interests" logo/label shown only once
    // (on the first item of the combined group).
    get thirdPartyGroupItems(): CardItem[] {
        const combined = [
            ...this.thirdPartyItems,
            ...this.designatedThirdPartyItems,
            ...this.mortgageItems,
            ...this.escrowItems,
        ];
        return combined.map((item, index) =>
            index === 0
                ? { ...item, icon: 'third-party-interest', label: 'Third party interests ' }
                : item
        );
    }

    navigateToAMS(producerCode: string) {
        window.open(`${this.config.amsPortalUrl.replace('<producerCode>', producerCode)}`, '_blank');
    }

    retryDCPolicy() {
        this.retry.emit();
    }

    openEditPopup(field: CardField) {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        const rawPolicyEffDate =
            policyState.data?.[policyNum]?.policySnapshot?.policyGeneralInformation?.effectiveDate?.value ?? '';
        const isLLC = this.isRestrictedOwnership(
            policyState.data?.[policyNum]?.policySnapshot?.policyGeneralInformation?.ownership?.value
        );

        // Detect if click came from the LLC Additional Insured (first entry) phone/email edit icon
        const isLLCAdditionalInsuredEdit = (field as any).actionContext === 'llcAdditionalInsured';

        const today = moment().startOf('day');

        // Determine effective date from PhoneSearch termData:
        //   - Multiple terms               → today
        //   - Single term, start_date > today  → policy effective date (start_date)
        //   - Single term, start_date <= today → today
        const termData: { term: string; startDate: string; endDate: string }[] =
            policyState.termData ?? [];
        let anchor: moment.Moment;
        if (termData.length > 1) {
            // Multiple terms — always use today
            anchor = today;
        } else if (termData.length === 1) {
            const singleTerm = termData[0];
            const termStartDate = singleTerm.startDate
                ? moment(singleTerm.startDate, 'YYYY-MM-DD').startOf('day')
                : null;
            if (termStartDate && termStartDate.isAfter(today)) {
                // Single future term — use the policy's start date
                anchor = termStartDate;
            } else {
                // Single current/past term — use today
                anchor = today;
            }
        } else {
            // termData not yet loaded — fall back to DC policy effective date logic
            const policyEffDate = rawPolicyEffDate
                ? moment(rawPolicyEffDate, 'YYYY-MM-DD').startOf('day')
                : today;
            anchor = policyEffDate.isAfter(today) ? policyEffDate : today;
        }

        const defaultDate = anchor.format('MM/DD/YYYY');
        const minDate = anchor.format('MM/DD/YYYY');
        const maxDate = anchor.format('MM/DD/YYYY');

        // Named insured (LLC): phone hidden — email only
        // Additional Insured (LLC, first entry): phone only — email not editable
        // Non-LLC (named insured): both phone + email shown
        const phoneFields = (isLLC && !isLLCAdditionalInsuredEdit) ? [] : [
            {
                type: 'text',
                caption: 'Phone Number',
                name: 'mobileNumber',
                id: 'mobileNumber',
                placeholder: 'Enter phone number',
                required: false,
                keyboardFilter: 'pint',
                maxLength: 10,
                value: '',
                validationMessages: [
                    { type: 'phoneService', message: 'This phone number is invalid. Please double-check it.' },
                    { type: 'atLeastOne', message: 'Please enter at least a phone number or email.' },
                ],
            },
            {
                type: 'button',
                caption: 'Verify Phone',
                name: 'verifyPhone',
                buttonType: 'borderless',
                imageSrc: '',
            },
        ];

        // Email fields shown for named insured (LLC or non-LLC), hidden for LLC additional insured
        const emailFields = isLLCAdditionalInsuredEdit ? [] : [
            {
                type: 'text',
                caption: 'Email',
                name: 'email',
                id: 'email',
                placeholder: 'Enter email address',
                required: false,
                keyboardFilter: 'email',
                value: '',
                validationMessages: [
                    { type: 'emailService', message: 'This email address is invalid. Please check and try again.' },
                    { type: 'atLeastOne', message: 'Please enter at least a phone number or email.' },
                ],
            },
            {
                type: 'button',
                caption: 'Verify Email',
                name: 'verifyEmail',
                buttonType: 'borderless',
                imageSrc: '',
            },
        ];

        const popup = {
            type: 'actionDialog',
            emailVerified: false,
            useContactVerify: true,
            messageTitle: 'Edit Contact Information',
            messageSubtitle: isLLCAdditionalInsuredEdit
                ? 'Update the LLC member contact details below.'
                : 'Update the insured contact details below.',
            confirm: 'Save',
            cancel: 'Cancel',
            fields: [
                {
                    type: 'text',
                    caption: 'Effective Date',
                    name: 'effectiveDate',
                    id: 'effectiveDate',
                    placeholder: 'MM/DD/YYYY',
                    required: true,
                    value: defaultDate,
                    min: minDate,
                    max: maxDate,
                },
                ...phoneFields,
                ...emailFields,
            ],
        };
        this.modalService.confirm(popup).subscribe((result: any) => {
            if (!result || result[0] !== 'Save') return;
            const formValue = result[1];
            if (!formValue) return;
            // Named insured LLC: phone always empty. Additional insured LLC or non-LLC: use form value
            const phone = (isLLC && !isLLCAdditionalInsuredEdit) ? '' : (formValue.mobileNumber ?? '');
            // LLC additional insured: email not editable — always send empty string
            const email = isLLCAdditionalInsuredEdit ? '' : (formValue.email ?? '');
            if (!phone && !email) return;
            const rawDate = formValue.effectiveDate;
            const effectiveDate = rawDate ? moment(rawDate).format('YYYY-MM-DD') : '';
            const policyNum = this.policySignal().policyNumber;
            const quoteId = this.policySignal().data?.[policyNum]?.policySnapshot?.policyGeneralInformation?.quoteId?.value ?? '';
            const ownership = this.policySignal().data?.[policyNum]?.policySnapshot?.policyGeneralInformation?.ownership?.value ?? '';
            const updating = new Set<'phone' | 'email'>();
            if (phone) updating.add('phone');
            if (email) updating.add('email');
            this.updatingField.set(updating);

            // Correlate the user action with the API call it triggers so both
            // land in Elasticsearch under one correlationId (PRODSUPP-145765).
            const correlationId = this.trackingService.newCorrelationId();
            this.trackingService.trackUserAction({
                name: 'edit-contact.submit',
                correlationId,
                metadata: {
                    policyNumber: policyNum,
                    fields: Array.from(updating),
                    ownership,
                },
            });

            this.editContactService
                .editContact(policyNum, effectiveDate, phone, email, quoteId, ownership, correlationId)
                .subscribe({
                next: () => {
                    this.updatingField.set(new Set());
                    this.toastService.add({
                        type: 'success',
                        title: '',
                        message: 'Contact preferences updated successfully.',
                    });
                    const snapshot = this.policySignal().data?.[policyNum]?.policySnapshot?.policyGeneralInformation;
                    this.store.dispatch(
                        PolicyActions.refreshDCPolicy({
                            policyNumber: policyNum,
                            effectiveDate: snapshot?.effectiveDate?.value ?? undefined,
                            expireDate: snapshot?.expirationDate?.value ?? undefined,
                        })
                    );
                },
                error: () => {
                    this.updatingField.set(new Set());
                    this.toastService.add({
                        type: 'error',
                        title: '',
                        message: 'Failed to update contact preferences. Please try again.',
                    });
                },
            });
        });
    }

    private getStatusTheme(status: string): string {
        switch (status?.toLowerCase()) {
            case 'inforce':
                return 'neutralAq';
            case 'new':
                return 'neutralPurple';
            case 'expired':
                return 'default';
            case 'cancelled':
                return 'default';
            case 'cancel-pending':
                return 'neutralAq';
            case 'nonrenewed':
                return 'default';
            default:
                return 'default';
        }
    }
}
